import React from 'react'
import { Collation } from 'common/models/typings/Collation'
import { ShipmentTour } from 'common/models/typings/Tour'

type ExtendedCollation = Collation & { tourColor: string; isSelected: boolean }

interface InfoWindowProps {
  collation: ExtendedCollation
  tours: ShipmentTour[]
  onMoveToTour: (
    currentTourId: string,
    newTourId: string,
    collationId: string,
  ) => void
  onHideTour: (tourId: string) => void
}

export default function createInfoWindowHTML(
  collation: ExtendedCollation,
  tours: ShipmentTour[],
  onMoveToTour: (
    currentTourId: string,
    newTourId: string,
    collationId: string,
  ) => void,
  onHideTour: (tourId: string) => void,
) {
  const address = collation.recipient?.location?.address || ''
  const postalCode = collation.recipient?.location?.postalCode || ''
  const city = collation.recipient?.location?.city || ''

  const currentTour = tours.find((tour) =>
    tour.collations.some((c) => c.id === collation.id),
  )
  const currentTourId = currentTour?.id || ''
  const collationId = collation.id || ''

  const otherTours = tours
    .filter((tour) => tour.id !== currentTourId)
    .map((tour) => ({
      id: tour.id,
      color: tour.color,
      number: tour.number || 1,
    }))

  const isSingleTour = tours.length === 1

  const createTourMarkerSVG = (tour: {
    id: string | number
    color: string
    number: number
  }) => {
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="32" viewBox="0 0 24 32">
        <path d="M12 0C5.4 0 0 5.4 0 12c0 6.6 12 20 12 20s12-13.4 12-20C24 5.4 18.6 0 12 0z" 
              fill="${tour.color}" 
              stroke="#000" 
              stroke-opacity="0.8" 
              stroke-width="1.5"/>
        <text text-anchor="middle" 
              fill="#fff" 
              font-size="10" 
              font-family="ClanPro-WideBold" 
              font-weight="bold"
              x="12" 
              y="16" 
              dominant-baseline="middle">${tour.number}</text>
      </svg>`,
    )}`
  }

  const moveToTourSection =
    otherTours.length > 0
      ? `
    <p style="margin: 8px 0 4px 0; font-size: 12px;">Move to tour:</p>
    <div style="display: flex; width: 100%; gap: 4px; flex-wrap: wrap;">
      ${otherTours
        .map(
          (tour) => `
        <img 
          style="cursor: pointer; width: 24px; height: 32px;" 
          src="${createTourMarkerSVG(tour)}"
          data-tour-id="${currentTourId}"
          data-new-tour-id="${tour.id}"
          data-collation-id="${collationId}"
          class="info-window move-tour"
          title="Move to tour ${tour.number}"
        />
      `,
        )
        .join('')}
    </div>
  `
      : ''

  const hideTourSection = !isSingleTour
    ? `
    <div 
      style="color: var(--plannr-blue, #007bff); margin-top: 12px; font-size: 12px; cursor: pointer;"
      class="info-window hide-tour"
      data-tour-id="${currentTourId}"
    >
      <svg width="16" height="16" style="vertical-align: middle; margin-right: 4px;" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
      </svg>
      Hide tour
    </div>
  `
    : ''

  return `
    <div style="max-width: 200px; display: flex; flex-direction: column; gap: 4px;">
      <div style="display: flex; flex-direction: row; gap: 4px; align-items: center;">
        <div style="background: ${collation.tourColor}; padding: 4px 8px; border-radius: 8px; color: #fff;">Tour ${currentTour?.number}</div>
      </div>
      <strong>${address}, ${postalCode}, ${city}</strong>
      ${moveToTourSection}
      ${hideTourSection}
    </div>
  `
}
